import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Pipe,
  PipeTransform,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ANIMATION_CONSTANTS, RivSelect } from '@rivet-health/design-system';
import {
  RivvyMessage,
  RivvyMessageType,
  RivvyState,
  RivvyThread,
} from '@rivethealth/data/rivvy';
import { marked } from 'marked';
import { firstValueFrom, of, Subscription } from 'rxjs';
import { RivvyApiService } from '../../api/rivvy.service';
import { AccountState, useAccountId } from '../../app/account/account.state';
import { HookDirective } from '../core/hook';
import {
  useEffect,
  useLoad,
  useMemo,
  useState,
  useSubscription,
  useSubscriptionLatest,
} from '../core/hooks';
import { RouteService } from '../state/route.service';
import { RivvyOutletService } from './rivvy-outlet.service';

@Pipe({ name: 'safeHtml' })
export class SafeHtmlPipe implements PipeTransform {
  constructor(private sanitized: DomSanitizer) {}
  transform(value: string) {
    return this.sanitized.bypassSecurityTrustHtml(value);
  }
}

@Component({
  selector: 'rivvy-outlet',
  styleUrls: ['./rivvy-outlet.component.scss'],
  templateUrl: './rivvy-outlet.component.html',
  providers: [AccountState, RouteService],
})
export class RivvyOutletComponent extends HookDirective {
  @ViewChild('rivvyInputField') rivvyInputField!: ElementRef;

  constructor(
    public rivvyOutletService: RivvyOutletService,
    private rivvyApiService: RivvyApiService,
    private routeService: RouteService,
    changeDetector: ChangeDetectorRef,
  ) {
    marked.setOptions({
      gfm: true, // Enable GitHub-flavored markdown
      breaks: true, // Enable line breaks
    });
    super(changeDetector);
  }

  private sub!: Subscription;

  readonly hook = ANIMATION_CONSTANTS.SIDE_SHEET.HOOK;

  summonedFunctionality = () => {
    const accountId = useAccountId(this.routeService);

    const [showSuggestions, setShowSuggestions] = useState(false);

    const [theRobotIsTalking, setTheRobotIsTalking] = useState(false);

    const [inputValue, setInputValue] = useState('');

    const [editingThread, setEditingThread] = useState(false);

    const [threadsLoadable, fireThreadsLoadTrigger] = useLoad(
      () => this.rivvyApiService.getThreads(accountId),
      [],
    );

    const threads = threadsLoadable.data ?? [];
    const [currentThread, setCurrentThread] = useState<RivvyThread | undefined>(
      undefined,
    );

    const [messagesLoadable, fireMessagesLoadTrigger] = useLoad(() => {
      if (!currentThread || !currentThread.id) {
        return of(null);
      }
      return this.rivvyApiService.getThreadMessages(
        accountId,
        currentThread?.id,
      );
    }, [currentThread]);
    const chatHistory = messagesLoadable.data ?? [];

    useEffect(() => {
      if (threads.length < 1) {
        fireThreadsLoadTrigger();
        return;
      }
      if (threads.length > 0 && !currentThread) {
        setCurrentThread(threads[0]);
        touchThread(threads[0]);
      }
    }, [threads]);

    const threadsManager = useMemo(
      () =>
        RivSelect.createInMemoryManager(
          () => [{ header: 'Threads', options: threads }],
          {},
        ),
      [threadsLoadable.data],
    );

    const threadsState = useSubscriptionLatest(threadsManager.state, [
      threadsManager,
    ]);

    useSubscription(
      threadsManager.actions,
      {
        next: action => {
          if (action.type === 'setSelectedOption') {
            const thread = threads.find(t => t.id === action.id);
            if (!thread) return;
            setCurrentThread(thread);
            touchThread(thread);
          }
        },
      },
      [threadsManager],
    );

    const onNewThread = async () => {
      await firstValueFrom(this.rivvyApiService.createThread(accountId));
      refreshThreads();
    };

    const onDeleteThread = async (thread?: RivvyThread) => {
      if (!thread) return;
      await firstValueFrom(
        this.rivvyApiService.deleteThread(accountId, thread?.id),
      );
      refreshThreads();
    };

    const onEditThread = () => {
      setEditingThread(!editingThread);
      setTimeout(() => {
        // dom needs to render
        const input = document.querySelector(
          '#threadTitleInput input',
        ) as HTMLInputElement;
        input?.click();
        input?.focus();
        input?.setSelectionRange(0, input.value.length);
      }, 1);
    };

    const refreshThreads = () => {
      setCurrentThread(undefined);
      setInputValue('');
      fireThreadsLoadTrigger();
      fireMessagesLoadTrigger();
    };

    const touchThread = async (thread?: RivvyThread) => {
      if (!thread) return;
      await firstValueFrom(
        this.rivvyApiService.modifyThread(accountId, thread.id, thread.title),
      );
    };

    const formatContent = (content?: RivvyMessage['content']) => {
      if (!content) return '';
      const parsed = marked.parse(content);
      return this.addIndentationMargins(parsed);
    };

    const onSendMessage = (thread?: RivvyThread, suggestion?: string) => {
      if (!thread) return;
      if (theRobotIsTalking) return;
      if (isLimited) return;
      setTheRobotIsTalking(true);
      if (suggestion) {
        chatHistory.push({
          content: suggestion,
          source: RivvyMessageType.HUMAN,
          created: new Date().toISOString(),
          loading: false,
        });
      } else if (this.hasNonWhitespace(inputValue)) {
        chatHistory.push({
          content: inputValue,
          source: RivvyMessageType.HUMAN,
          created: new Date().toISOString(),
          loading: false,
        });
      }
      if (inputValue === '' && !suggestion) {
        setTheRobotIsTalking(false);
      } else {
        setTheRobotIsTalking(true);
        // add a robot is loading message
        chatHistory.push({
          content: '',
          source: RivvyMessageType.AI,
          created: new Date().toISOString(),
          loading: true,
        });

        if (this.sub) {
          this.sub.unsubscribe();
        }
        if (!thread) {
          return;
        }
        const sendValue = suggestion ?? inputValue;
        setInputValue('');
        if (
          chatHistory.length == 2 &&
          currentThread &&
          currentThread.title == 'New thread'
        ) {
          currentThread.title = sendValue; // mirrors what the db
        }
        this.sub = this.rivvyApiService
          .postQueryStream(
            accountId,
            {
              source: RivvyMessageType.HUMAN,
              content: sendValue,
              created: new Date().toISOString(), // ISO timestamp
            },
            thread?.id,
          )
          .subscribe({
            next: msg => {
              const lastMessage = chatHistory[chatHistory.length - 1];
              this.parsingBuffer += msg;
              if (this.parsingBuffer.includes('\n')) {
                this.fullResponse += this.parsingBuffer;
                lastMessage.content = this.fullResponse;
                this.parsingBuffer = '';
              }
              lastMessage.loading = false;
            },
            error: err => {
              const lastMessage = chatHistory[chatHistory.length - 1];
              lastMessage.loading = false;
              lastMessage.content = 'Something went wrong, please try again';
            },
            complete: () => {
              const lastMessage = chatHistory[chatHistory.length - 1];
              this.fullResponse += this.parsingBuffer;
              const parsed = marked.parse(this.fullResponse);
              lastMessage.content = this.addIndentationMargins(parsed);
              this.parsingBuffer = '';
              this.fullResponse = '';
              setTheRobotIsTalking(false);
            },
          });
      }
    };

    const closeModal = () => {
      const threadsSelect = document.querySelector(
        '#threadsSelect .scrim',
      ) as HTMLInputElement;
      threadsSelect?.click();
      this.invalidateExtensionExistsCache();
      this.rivvyOutletService?.dismiss();
    };

    const setTitle = (title: string) => {
      if (!currentThread) return;
      currentThread.title = title;
    };

    const [limits] = useLoad(
      () => this.rivvyApiService.getLimits(accountId),
      [accountId],
    );

    const isLimited = limits.data?.state === RivvyState.LIMITED;

    const warn = limits.data?.state === RivvyState.WARN;

    return {
      hasBeenSummoned: true,
      onSendMessage,
      showSuggestions,
      theRobotIsTalking,
      inputValue,
      setInputValue,
      setShowSuggestions,
      threads,
      threadsManager,
      onNewThread,
      onDeleteThread,
      threadsState,
      currentThread,
      editingThread,
      onEditThread,
      touchThread,
      chatHistory,
      formatContent,
      closeModal,
      setTitle,
      isLimited,
      warn,
    };
  };

  get _() {
    const hasBeenSummoned = useSubscriptionLatest(
      this.rivvyOutletService.hasBeenSummoned$,
      [],
    );

    if (hasBeenSummoned) {
      return this.summonedFunctionality();
    } else
      return {
        hasBeenSummoned: false,
        onSendMessage: () => {},
        showSuggestions: false,
        theRobotIsTalking: false,
        inputValue: '',
        setInputValue: () => {},
        setShowSuggestions: () => {},
        threads: [],
        threadsManager: null,
        onNewThread: () => {},
        onDeleteThread: () => {},
        threadsState: {},
        currentThread: {
          title: '',
          prompt: '',
        } as RivvyThread,
        editingThread: false,
        onEditThread: () => {},
        touchThread: () => {},
        chatHistory: [],
        formatContent: () => '',
        closeModal: () => {},
        setTitle: () => {},
        isLimited: true,
        warn: false,
      };
  }

  showSuggestions = false;
  parsingBuffer = '';
  fullResponse = '';
  helpfulDisclaimer =
    'AI may provide inaccurate information. Please research and confirm important matters.';

  suggestions = [
    'Why would CPT 99214 be denied for medical necessity?',
    'Our Medicare claims keep denying with CARC 16 — what should I look for?',
    'Can you explain the difference between COB and subrogation?',
    'How do I know when to use modifier 25?',
  ];

  hasNonWhitespace = (text: string): boolean => {
    return text.trim().length > 0;
  };

  traverse = (node: HTMLElement, depth: number) => {
    const indentMargin = `${(depth + 1) * 0.75}rem`;

    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      if (['ul', 'ol', 'li', 'p', 'div'].includes(tag)) {
        const existingStyle = node.getAttribute('style') || '';
        const newStyle = `margin-left:${indentMargin};${existingStyle}`;
        node.setAttribute('style', newStyle);
      }
    }

    for (const child of Array.from(node.children)) {
      this.traverse(child as HTMLElement, depth + 1);
    }
  };

  addIndentationMargins = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    for (const child of Array.from(doc.body.children)) {
      this.traverse(child as HTMLElement, 0);
    }

    return doc.body.innerHTML;
  };

  trackByIndex(index: number): number {
    return index;
  }

  private _extensionExistsCache: boolean | null = null;

  extensionExists(): boolean {
    if (this._extensionExistsCache !== null) {
      return this._extensionExistsCache;
    }
    const divs = document.querySelectorAll('body > div');
    for (const div of Array.from(divs)) {
      const shadowRoot = (div as HTMLElement).shadowRoot;
      if (shadowRoot && shadowRoot.querySelector('.dashboard')) {
        this._extensionExistsCache = true;
        return true;
      }
    }
    this._extensionExistsCache = false;
    return false;
  }

  invalidateExtensionExistsCache(): void {
    this._extensionExistsCache = null;
  }

  RivvyMessageType = RivvyMessageType;

  openIntercom() {
    Intercom('show');
  }
}
